import { trpc } from '@tencent/tpro_rmonitor_web_server_basic';
import {
  get,
  isString,
} from 'lodash-es';
import {
  formatAllMallocSize,
  formatMemorySize,
} from '@/infrastructure/format-data';
import {
  BASE_NAME,
  toSearchStr,
} from '@/infrastructure/history';
import { formatNum } from '@/infrastructure/utils/digit';
import { calculatePercentage } from '@/infrastructure/utils/issue';
import { formatTimestamp } from '@/infrastructure/utils/timestamp';
import { decimalPermyriad } from '@/infrastructure/utils/to-unit';
import { RawTotalCount } from '@/presentation/components/exception-issue-list/api';
import { COMPARE_STATUS } from '@/presentation/components/issue-list/components/compare-status';
import { getSelfLaunchKeyMethods } from '@/presentation/pages/compliance/self-launch/utils';
import { getApiInfo } from '@/presentation/pages/compliance/utils';
import {
  AUTO_CALL_OPTION,
  AUTO_CALL_SELF_OPTION,
  FieldKey,
  getLocalFieldLabelByValue,
  ISSUE_STATUS_COMPLIANCE_DEPRECATED_OPTION,
  ISSUE_STATUS_COMPLIANCE_LITE_OPTION,
  ISSUE_STATUS_COMPLIANCE_OPTION,
  Zone,
} from '@/proto/field';
import { WithTypeCast } from '@raft/utils';
type Tag = WithTypeCast<trpc.rmonitor.web_server.Tag>;

const renderUnitMs = (value: number) => `${Number(value)?.toFixed(2)} ms`;
const renderUnitHr = (value: number) => `${(value / 60 / 60 / 1000)?.toFixed(6)} h`;
const renderUnitMB = (value: number) => `${value?.toFixed?.(2)} MB`;
const renderUnitKB = (value: number) => `${value?.toFixed?.(2)} KB`;
const renderMallocSize = (value: number) => `${formatMemorySize(Number(value) || 0)}`;
const renderAllMallocSize = (value: number) => `${formatAllMallocSize(Number(value) || 0)}`;
const renderRate = (val: number) => `${(val * 100).toFixed(2)}%`;
const renderPercentage = (val: string) => `${(Number.parseFloat(val) * 100)?.toFixed(2)}%`;
const renderProblemLevel = (data: Record<string, any>) => (data?.riskType ? (`${data.riskType}: ${data.riskName} `) : '未定义 ');
const renderSixElementTypeList = (data: Record<string, any>) => data.sixElementTypeList.join(',');
const renderProblemType = (data: Record<string, any>) => {
  const problemType = getLocalFieldLabelByValue(FieldKey.ProblemType, data?.problemType);
  return `级别：${renderProblemLevel(data)}\n类型：${problemType}`;
};

const renderKeyMethods = (type: Zone, data: Record<string, any>) => {
  if (type === Zone.ComplianceDownload) {
    return `
聚类链接：${data.webUrl}
是否有下载按钮：${data.downloadBtn === 1 ? '是' : '否'}
APK下载链接：${data.apkUrl}
    `;
  }
  if (type === Zone.ComplianceSelfLaunch) {
    return getSelfLaunchKeyMethods(data).join('\n');
  }
  if (type === Zone.ComplianceNetwork) {
    return data.url;
  }

  if (Array.isArray(data?.keyMethodList)) {
    return data.keyMethodList.join('\n');
  }
  if (Array.isArray(data.keyMethods)) {
    return data.keyMethods.join('\n');
  }
  return data.keyMethods;
};

const renderIssueStatus = (type: Zone, data: string) => {
  if (type === Zone.Compliance) {
    return ISSUE_STATUS_COMPLIANCE_OPTION.find(option => option.value === data)?.label
      || ISSUE_STATUS_COMPLIANCE_DEPRECATED_OPTION.find(option => option.value === data)?.label
      || '待评估';
  }
  if (type === Zone.ComplianceNetwork || type === Zone.ComplianceSelfLaunch) {
    return ISSUE_STATUS_COMPLIANCE_LITE_OPTION.find(option => option.value === data)?.label || '待评估';
  }
  return getLocalFieldLabelByValue(FieldKey.IssueStatus, data);
};

const renderSixElements = (data: Record<string, any>) => `
  APP名称：${data.appName}
  APP版本：${data.appVer}
  APP隐私：${data.appPrivacy}
  APP权限：${data.appPerm}
  APP开发者：${data.appAuthor}
  ${data.apkIsGame ? `游戏运营商：${data.gameCp || '不涉及'}` : ''}`;

const renderSceneDistribution = (data: Record<string, any>) => `
  页面不允许调用：${data.illegalCount || 0}
  隐私协议同意前调用：${data.beforeCountShow || 0}
  非前台调用：${data.backCountShow || 0}
  高频调用：${data.highFreqCount || 0}
  前台静默调用：${data.silenceCount || 0}
  未授权调用：${data.denyRetryCount || 0}
  正常调用：${data.normalCount || 0}`;

const renderPrivacyFunc = (data: any) => {
  if (data?.privacyType && data?.privacyFunc) {
    const apiInfo = getApiInfo(data.apiInfos, data?.privacyType, data.privacyFunc);
    return `分类：${apiInfo?.description} (${data.privacyType})\n函数：${apiInfo?.api || data.privacyFunc}`;
  }
  return '';
};

const renderAutoCallType = (data: string) => AUTO_CALL_OPTION.find(item => (
  item.value === data
))?.label || data;

const renderAutoCallSelf = (data: string) => AUTO_CALL_SELF_OPTION.find(item => (
  item.value === data
))?.label || data;

const renderCollectData = (data: string[]) => data?.join(',') || '';

const renderComplianceIssueUrl = (data: Record<string, any>) => {
  const { token, feature } = data;
  const queryStr = toSearchStr({
    token,
    feature,
  });
  return `${location.origin}${BASE_NAME}/compliance/issues/detail?${queryStr}`;
};

/**
 * 导出列格式化
 * @param dataIndex
 * @param rowData
 * @param type
 * @param totalCount
 */
const formatExportData = (
  dataIndex: string,
  rowData: Record<string, any>,
  type: Zone,
  totalCount?: RawTotalCount,
) => {
  const unitMsList = [
    // 卡顿独有字段
    'costTimeAverage',
    'timeCostP10',
    'timeCostP25',
    'timeCostP50',
    'keyMethodCostAverage',
    'keyMethodCostP10',
    'keyMethodCostP25',
    'keyMethodCostP50',
    'methodMaxCostP50',
    'costTimeAverageValueA',
    'costTimeAverageValueB',
    'timeCostP10ValueA',
    'timeCostP10ValueB',
    'timeCostP25ValueA',
    'timeCostP25ValueB',
    'timeCostP50ValueA',
    'timeCostP50ValueB',
    'keyMethodCostAverageValueA',
    'keyMethodCostAverageValueB',
    'keyMethodCostP10ValueA',
    'keyMethodCostP10ValueB',
    'keyMethodCostP25ValueA',
    'keyMethodCostP25ValueB',
    'keyMethodCostP50ValueA',
    'keyMethodCostP50ValueB',
    'methodMaxCostP50ValueA',
    'methodMaxCostP50ValueB',
  ];
  const unitHrList = [
    // 卡顿独有字段
    'totalCost',
    'keyMethodTotalCost',
    'totalCostValueA',
    'totalCostValueB',
    'keyMethodTotalCostValueA',
    'keyMethodTotalCostValueB',
  ];
  const unitKBList = [
    // java独有字段
    'leakBytesMax',
    'leakBytesMaxValueA',
    'leakBytesMaxValueB',
    // 大图分析独有字段
    'overDecodeSize',
    'overDecodeSizeValueA',
    'overDecodeSizeValueB',
  ];
  const unitMbList = [
    // native独有字段
    'maxStackSize',
    'maxPssSize',
    'maxStackSizeValueA',
    'maxStackSizeValueB',
    'maxPssSizeValueA',
    'maxPssSizeValueB',
    // 大块内存独有字段
    'p90Malloc',
    'maxMalloc',
    'maxMallocValueA',
    'maxMallocValueB',
    'p90MallocValueA',
    'p90MallocValueB',
    // 内存图独有字段
    'memorySize',
  ];
  const rateList = [
    // 大图分析独有字段
    'overDecodeRate',
    'overDecodeRateValueA',
    'overDecodeRateValueB',
  ];
  const mallocSizeList = [
    // foom独有字段
    'p90MaxMallocSize',
    'p50MaxMallocSize',
    'avgMaxMallocSize',
    'p90MemPeak',
    'p50MemPeak',
    'avgMemPeak',
    'p90MemPeakValueA',
    'p90MemPeakValueB',
    'p90MemPeakVariation',
    'p50MemPeakValueA',
    'p50MemPeakValueB',
    'p50MemPeakVariation',
    'avgMemPeakValueA',
    'avgMemPeakValueB',
    'avgMemPeakVariation',
    'p90MaxMallocSizeValueA',
    'p90MaxMallocSizeValueB',
    'p90MaxMallocSizeVariation',
    'p50MaxMallocSizeValueA',
    'p50MaxMallocSizeValueB',
    'p50MaxMallocSizeVariation',
    'avgMaxMallocSizeValueA',
    'avgMaxMallocSizeValueB',
    'avgMaxMallocSizeVariation',
    'allMallocSizeValueA',
    'allMallocSizeValueB',
    'allMallocSizeVariation',
    // foom分配独有字段
    'p90MallocSize',
    'p50MallocSize',
    'avgMallocSize',
    'p90MallocSizeValueA',
    'p90MallocSizeValueB',
    'p90MallocSizeVariation',
    'p50MallocSizeValueA',
    'p50MallocSizeValueB',
    'p50MallocSizeVariation',
    'avgMallocSizeValueA',
    'avgMallocSizeValueB',
    'avgMallocSizeVariation',
  ];
  const allMallocSizeList = [
    // foom独有字段
    'allMallocSize',
  ];
  const data = get(rowData, dataIndex);
  if (unitMsList.includes(dataIndex)) {
    return renderUnitMs(data);
  }
  if (unitHrList.includes(dataIndex)) {
    return renderUnitHr(data);
  }
  if (unitKBList.includes(dataIndex)) {
    if (isNaN(data)) {
      return data;
    }
    return renderUnitKB(data);
  }
  if (unitMbList.includes(dataIndex)) {
    if (isNaN(data)) {
      return data;
    }
    return renderUnitMB(data);
  }
  if (rateList.includes(dataIndex)) {
    return renderRate(data);
  }
  if (mallocSizeList.includes(dataIndex)) {
    return renderMallocSize(data);
  }
  if (allMallocSizeList.includes(dataIndex)) {
    return renderAllMallocSize(data);
  }

  if ([Zone.Anr, Zone.Foom].includes(type)) {
    switch (dataIndex) {
      case 'issuesDocCountPerDevice':
        return rowData?.issuesDocCountPerDevice?.toFixed?.(2);
      case 'minAppVersionInFilter':
        return `${rowData.minAppVersionInFilter || '-'}${rowData.minAppVersionInFilter
        !== rowData.maxAppVersionInFilter
          ? `~${rowData.maxAppVersionInFilter || ''}` : ''}`;
      case 'tagsInFilter':
        return rowData.tagsInFilter?.map?.((item: { name: any; }) => item?.name)?.join?.('、') || '';
    }
  }

  // 公共字段转换，例如lastReportTime为时间戳转换为YYYY-MM-DD HH:mm:ss等
  switch (dataIndex) {
    case 'keyMethods':
      return renderKeyMethods(type, rowData);
    case 'lastReportTime':
      return formatTimestamp(data, 'YYYY-MM-DD HH:mm:ss');
    case 'reportTime':
      return formatTimestamp(data, 'YYYY-MM-DD HH:mm:ss');
    case 'compareStatus':
      return COMPARE_STATUS.getDescFromValue(data)?.text;
    case 'tapdStatus':
      return getLocalFieldLabelByValue(FieldKey.TapdStatus, data);
    case 'issueStatus':
      return renderIssueStatus(type, data);
    case 'proportionValueA':
      return decimalPermyriad(Number(data), 2, false);
    case 'proportionValueB':
      return decimalPermyriad(Number(data), 2, false);
    case 'privacyFunc':
      return renderPrivacyFunc(rowData);
    case 'sceneDistribution':
      return renderSceneDistribution(rowData);
    case 'percentage':
      return renderPercentage(data);
    case 'riskType':
      return renderProblemType(rowData);
    case 'privacy':
      return renderPrivacyFunc(rowData);
    case 'complianceIssueUrl':
      return renderComplianceIssueUrl(rowData);
    case 'countRatio':
      return renderPercentage(data);
    case 'sixElementTypeList':
      return renderSixElementTypeList(rowData);
    case 'sixElements':
      return renderSixElements(rowData);
    case 'autoCallType':
      return renderAutoCallType(data);
    case 'autoCallSelf':
      return renderAutoCallSelf(data);
    case 'collectData':
      return renderCollectData(data);
    case 'tags':
      return data.map((tag: Tag) => tag.name)?.join(',');
    case 'docCount':
      return `${formatNum(data || 0)}（${calculatePercentage(data, totalCount?.totalDocCount, 100, 4)}%）`;
    case 'qimeiCount':
      return `${formatNum(data || 0)}（${calculatePercentage(data, totalCount?.totalDocCount, 100, 4)}%）`;
    case 'minAppVersionInFilter':
      return `${rowData.minAppVersionInFilter || '-'}${rowData.minAppVersionInFilter !== rowData.maxAppVersionInFilter ? `~${rowData.maxAppVersionInFilter || ''}` : ''}`;
    case 'tagsInFilter':
      return data?.map?.((item: any) => item?.name)?.join?.('、') || '';
    default:
      return data ;
  }
};

// 禁止导出列
const disableExportList = [
  { head: '序号', dataIndex: 'issueIndex' },
];

/**
 * 默认导出列
 * @param type
 * @param selectRow
 * @param column
 * @param totalCount
 * @param getDetailsUrl 获取详情链接
 *
 */
export const getExport = (
  type: Zone,
  selectRow: Record<string, any>[],
  column: Record<string, any>[],
  totalCount?: RawTotalCount,
  getDetailsUrl?: (record: Record<string, any>) => string,
) => {
  let head: string[];
  let fieldsList: string[];
  // 列配置表头，title为element时取key作为表头
  const titleList = column.map(item => (isString(item.title) ? item.title : item.key ?? ''))
    .filter(title => disableExportList.find(disableExport => title !== disableExport.head));
  const dataIndexList = column.map(item => item.dataIndex)
    .filter(dataIndex => disableExportList.find(disableExport => disableExport.dataIndex !== dataIndex));
  if (type === Zone.MemGraph) {
    head = titleList;
    fieldsList = dataIndexList;
  } else if (type === Zone.Compliance) {
    head = ['issue ID', '问题链接', ...titleList];
    fieldsList = ['feature', 'complianceIssueUrl', ...dataIndexList];
  } else if (type === Zone.Net) {
    head = titleList;
    fieldsList = dataIndexList;
  } else {
    head = ['issue ID', ...titleList];
    fieldsList = ['feature', ...dataIndexList];
  }


  if (getDetailsUrl) {
    head.push('问题详情链接');
    const body = selectRow.map((rowData) => {
      const list = fieldsList
        .map(dataIndex => formatExportData(dataIndex as string, rowData, type, totalCount));
      list.push(getDetailsUrl(rowData));
      return list;
    });
    return ({
      head,
      body,
    });
  }
  const body = selectRow.map(rowData => fieldsList
    .map(dataIndex => formatExportData(dataIndex as string, rowData, type, totalCount)));
  return ({
    head,
    body,
  });
};

export const PAGE_SIZES = [10, 20, 50, 100];
